import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import ReactHookSelect from 'react-hook-select';
import { observer } from 'mobx-react';
import { userDetail } from 'Stores/userDetail';
import { ReactComponent as CrossIcon } from 'Assets/usageAnalytics/cross-icon.svg';
import { ReactComponent as EnabledMailIcon } from 'Assets/svg/enabled-mail-icon.svg';
import { ReactComponent as DropDownIcon } from 'Assets/svg/dropdown-icon.svg';
import { ReactComponent as SearchIcon } from 'Assets/svg/open-search.svg';
import { ReactComponent as LockIcon } from 'Assets/svg/lock-icon-new.svg';
import RadioButton from 'Components/common/RadioBtn';
import { ToggleType } from 'Components/common/Toggle';
import SetEmailNotification from 'Components/common/setEmailNotification/setEmailNotification';
import Input from 'Components/common/newInputField';
import Button from 'Components/common/Button';
import advSearchState from 'Stores/advSearch';
import {
  EMPTY_FUNCTION,
  time as timeList,
  MX_SAVED_SEARCH_EVENTS,
} from 'Utils/constants';
import { Utils } from 'Utils/utils';
import './styles.scss';

const renderDropDownIcon = ({ isDropDownOpen }) => {
  return <DropDownIcon className={isDropDownOpen ? 'open' : ''} />;
};

function returnCapitalizedWord(word = '') {
  if (word !== null) return word.charAt(0) + word.slice(1).toLowerCase();
  return '';
}

function getTime(time) {
  return timeList.find(data => data.value === time).label || time;
}

const SavedSearchPopUp = observer(props => {
  const {
    onClose,
    isEdit = false,
    data: initialData = {},
    onSubmit = EMPTY_FUNCTION,
    isAPILoading = false,
    history,
  } = props;

  const {
    templateName = '',
    frequency,
    ordinalDay,
    day,
    timeZone,
    time,
    status = '',
  } = initialData;

  const [selectedSearchItem, setSelectedSearchItem] = useState('');
  const [selectedSearch, setSelectedSearch] = useState('new');
  const [errorMessage, setErrorMessage] = useState({ new: '', replace: '' });
  const [enable, setEnable] = useState(status === 'ACTIVE');
  const [inputValue, setInputValue] = useState(templateName);
  const [emailNotification, setEmailNotification] = useState({
    selectedTime: frequency ? returnCapitalizedWord(frequency) : 'Weekly',
    ordinalDay: ordinalDay ? returnCapitalizedWord(ordinalDay) : 'First',
    day: day ? returnCapitalizedWord(day) : 'Monday',
    time: time || '11:00 AM',
    timeZone: timeZone || 'GMT',
  });

  const isEmailNotificationEnabled = Utils.isSavedSearchUpdateEnabled();

  const handleOnChange = value => {
    setSelectedSearch(value);
    setInputValue('');
    setSelectedSearchItem('');
    setErrorMessage({ new: '', replace: '' });
  };

  const handleOnChangeEmail = (type, value) => {
    setEmailNotification(prevState => {
      return {
        ...prevState,
        [type]: value,
      };
    });
  };

  const handleOnChangeInputField = value => {
    setInputValue(value);
    setErrorMessage({ new: '', replace: '' });
  };

  const onClickSave = () => {
    let isValid = true;
    if (isEdit) {
      if (inputValue.length === 0) {
        setErrorMessage({
          ...errorMessage,
          new: 'Please enter new search name',
        });
        isValid = false;
      }
    } else if (selectedSearch === 'new') {
      if (inputValue.length === 0) {
        setErrorMessage({ ...errorMessage, new: 'Please enter search name' });
        isValid = false;
      }
    } else if (selectedSearchItem === '') {
      setErrorMessage({
        ...errorMessage,
        replace: 'Please select a saved search',
      });
      isValid = false;
    }

    if (isValid) {
      const newFrequency = emailNotification.selectedTime.toUpperCase();
      onSubmit({
        templateName: inputValue,
        ...(newFrequency === 'MONTHLY' &&
          enable && {
            ordinalDay: emailNotification.ordinalDay.toUpperCase(),
          }),
        ...(enable && {
          status: 'ACTIVE',
          frequency: newFrequency,
          day: emailNotification.day.toUpperCase(),
          time: emailNotification.time,
          timeZone: emailNotification.timeZone,
        }),
        ...(!isEdit &&
          selectedSearch === 'replace' && {
            id: selectedSearchItem,
            templateName: userDetail.contactSavedSearches?.find(
              savedSearch => savedSearch.id === selectedSearchItem,
            )?.templateName,
          }),
      });
    }
  };

  useEffect(() => {
    Utils.mixpanelTrack(MX_SAVED_SEARCH_EVENTS.SAVED_SEARCH_POPUP_SHOWN, {
      resultsCount: advSearchState.searchResultCount,
      type: isEdit ? 'edit' : 'add',
    });
  }, []);

  return (
    <>
      <div
        className={`common-popup saved-search-popup${
          isEmailNotificationEnabled ? '' : ' email-notification-disabled'
        }${isEdit ? ' edit-saved-search' : ''}`}
      >
        <div className="save-search-header">
          <h4>{isEdit ? 'Edit Saved Search' : 'Save Search'}</h4>
          <div onClick={onClose}>
            <CrossIcon className="save-search-close-icon" />
          </div>
        </div>
        <>
          <div className="first-main-container">
            {isEdit ? (
              <Input
                error={errorMessage.new}
                label="Search Name"
                className="input-wrapper-edit"
                inputProps={{
                  onChange: handleOnChangeInputField,
                  value: inputValue,
                  placeholder: 'Name your saved search',
                }}
              />
            ) : (
              <>
                <div className="input-field">
                  <div className="search-radio-btn">
                    <RadioButton
                      label="New Search"
                      value="new"
                      name="newSearch"
                      id="newSearch"
                      onChange={handleOnChange}
                      checked={selectedSearch === 'new'}
                    />
                  </div>
                  <Input
                    error={errorMessage.new}
                    className={`${
                      selectedSearch !== 'new' ? 'new-search-disabled' : ''
                    } input-tag`}
                    inputProps={{
                      placeholder: 'Name your saved search',
                      onChange: handleOnChangeInputField,
                      value: inputValue,
                    }}
                  />
                </div>
                <div className="saved-list">
                  <div className="saved-search-list">
                    <RadioButton
                      disabled={userDetail.contactSavedSearches.length === 0}
                      label="Replace Existing Search"
                      value="replace"
                      name="replaceExistingSearch"
                      id="replaceExistingSearch"
                      onChange={handleOnChange}
                      checked={selectedSearch === 'replace'}
                    />
                  </div>
                  <ReactHookSelect
                    renderDropDownIcon={renderDropDownIcon}
                    enableSearch={userDetail.contactSavedSearches?.length > 10}
                    searchIcon={<SearchIcon className="search-icon" />}
                    showPlaceholderInOptions={false}
                    searchInputProps={{
                      placeholder: 'Search from saved lists name',
                    }}
                    selectWrapperProps={{
                      className: `${
                        selectedSearch !== 'replace'
                          ? 'saved-search-disabled'
                          : ''
                      } save-search-dropdown ${
                        errorMessage.replace ? 'saved-search-email-error' : ''
                      }`,
                    }}
                    options={
                      userDetail.contactSavedSearches?.length > 0
                        ? userDetail.contactSavedSearches?.map(data => {
                            return {
                              label: data.templateName,
                              value: data.id,
                            };
                          })
                        : []
                    }
                    value={selectedSearchItem ? [selectedSearchItem] : []}
                    placeholder="Select from saved list"
                    disabled
                    getValue={data => {
                      setErrorMessage(prevState => {
                        return {
                          ...prevState,
                          replace: '',
                        };
                      });
                      const newValue = data[0];
                      setSelectedSearchItem(newValue);
                      if (isEmailNotificationEnabled) {
                        const savedSearchData = userDetail.contactSavedSearches?.find(
                          savedSearch => savedSearch.id === newValue,
                        );

                        const isEmailScheduleAvailable =
                          savedSearchData.status === 'ACTIVE';
                        setEnable(isEmailScheduleAvailable);
                        if (isEmailScheduleAvailable)
                          setEmailNotification({
                            selectedTime: returnCapitalizedWord(
                              savedSearchData.frequency,
                            ),
                            ordinalDay:
                              returnCapitalizedWord(
                                savedSearchData.ordinalDay,
                              ) || 'First',
                            day: returnCapitalizedWord(savedSearchData.day),
                            time: savedSearchData.time,
                            timeZone: savedSearchData.timeZone,
                          });
                      }
                    }}
                  />
                  <span className="error">{errorMessage.replace}</span>
                  {userDetail.contactSavedSearches.length === 0 && (
                    <div className="no-saved-search">
                      <sup>*</sup>No saved searches available to replace
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="second-main-container">
            <div className="title-wrapper">
              <h5 className="mail-info-header">Email Notifications</h5>
              {!Utils.isLBPaidSubscription() && (
                <div className="advanced-feature-tag">
                  <LockIcon />
                  <span>Advanced Feature</span>
                </div>
              )}
            </div>
            <div className="mail-toogle-container">
              <div className="mail-toggle">
                <EnabledMailIcon />
                <p className="mail-notification-text">
                  Turn on email notification to be alerted when we add new
                  contacts that
                  <br />
                  match your search criteria
                </p>
                <div className="save-search-toggle">
                  <ToggleType
                    firstText={enable ? 'On' : 'Off'}
                    toggle={enable}
                    offer={false}
                    cbk={() => {
                      const newValue = !enable;
                      setEnable(newValue);
                      Utils.mixpanelTrack(
                        MX_SAVED_SEARCH_EVENTS.EMAIL_NOTIFICATION_TOGGLED,
                        {
                          resultsCount: advSearchState.searchResultCount,
                          enabled: newValue,
                          type: isEdit ? 'edit' : 'add',
                        },
                      );
                      setEmailNotification({
                        selectedTime: 'Weekly',
                        ordinalDay: 'First',
                        day: 'Monday',
                        time: '11:00 AM',
                        timeZone: 'GMT',
                      });
                    }}
                    showSharpPoint
                  />
                </div>
              </div>
              {enable && (
                <>
                  <div className="marginBothSides" />
                  <SetEmailNotification
                    customTitle="Email Frequency"
                    customClassName="save-search-time-container"
                    handleChangeRadio={handleOnChangeEmail}
                    selectedEmailValues={emailNotification}
                  />
                </>
              )}
            </div>
          </div>

          <div className="marginBothSides" />
          <div className="popup-footer">
            {enable &&
              isEmailNotificationEnabled &&
              (emailNotification.selectedTime === 'Weekly' ? (
                <p className="footerText">
                  {`This email is scheduled to send every week on ${
                    emailNotification.day
                  } at ${getTime(emailNotification.time)} ${
                    emailNotification.timeZone
                  }.`}
                </p>
              ) : (
                <p className="footerText">
                  {`This email is scheduled to send every month on ${
                    emailNotification.ordinalDay
                  } ${emailNotification.day} at ${getTime(
                    emailNotification.time,
                  )} ${emailNotification.timeZone}.`}
                </p>
              ))}
            {!isEmailNotificationEnabled && enable ? (
              <>
                <button
                  type="button"
                  className="plain-button"
                  onClick={() => setEnable(false)}
                >
                  No, Thanks!
                </button>
                <Button
                  className={`search-button${isAPILoading ? ' loading' : ''}`}
                  type="button"
                  btnText="Upgrade Now"
                  click={() => {
                    Utils.mixpanelTrack(
                      MX_SAVED_SEARCH_EVENTS.UPGRADE_CLICKED,
                      {
                        resultsCount: advSearchState.searchResultCount,
                        type: isEdit ? 'edit' : 'add',
                      },
                    );
                    history.push('/pricing');
                  }}
                />
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="cancel-button"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <Button
                  className={`search-button${isAPILoading ? ' loading' : ''}`}
                  type="button"
                  btnText="Save Search"
                  click={onClickSave}
                  loader={isAPILoading}
                />
              </>
            )}
          </div>
        </>
      </div>
      <div className="common-popup-mask common-back-ground" onClick={onClose} />
    </>
  );
});

export default withRouter(SavedSearchPopUp);
